import React, { useState, useEffect, useCallback, useMemo } from "react";
import { SunIcon, MoonIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import { Link as ScrollLink, scroller } from "react-scroll";
import gsap from 'gsap';

import { FiChevronRight } from "react-icons/fi";
import { FaLinkedin, FaInstagram, FaFlickr, FaEnvelope } from 'react-icons/fa';
import { ScaleIcon } from '@heroicons/react/24/outline';
import { FaFlask } from 'react-icons/fa';


import ukFlag from '../assets/uk.svg';
import ptFlag from '../assets/pt.svg';

const Header = ({ openLegalModal }) => {
    const { t, i18n } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);

    const sections = useMemo(() => ["intro", "about", "skills", "portfolio", "certificates", "contacts"], []);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

    useEffect(() => {
        const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        setIsDarkMode(isDark);
        if (isDark) {
            document.body.classList.add("dark");
        } else {
            document.body.classList.remove("dark");
        }
    }, []);

    useEffect(() => {
        if (window.location.hash === '#legal') {
            openLegalModal();
        }
    }, [openLegalModal]);

    const toggleDarkMode = () => {
        setIsDarkMode(prevMode => {
            const newMode = !prevMode;
            if (newMode) {
                document.body.classList.add("dark");
            } else {
                document.body.classList.remove("dark");
            }
            return newMode;
        });
    };

    const toggleLanguage = () => {
        const newLang = i18n.language === "en" ? "pt" : "en";
        i18n.changeLanguage(newLang);
    };

    const scrollToSection = useCallback((sectionId, index) => {
        const commonScrollOptions = {
            duration: 800,
            smooth: "easeInOutQuart",
            offset: 0,
        };

        if (menuOpen) {
            setMenuOpen(false);
            setTimeout(() => {
                scroller.scrollTo(sectionId, commonScrollOptions);
            }, 100);
        } else {
            scroller.scrollTo(sectionId, commonScrollOptions);
        }
        setCurrentSectionIndex(index);
    }, [menuOpen]);

    const handleLegalLinkClick = useCallback(() => {
        openLegalModal();
        if (menuOpen) {
            setMenuOpen(false);
        }
    }, [openLegalModal, menuOpen]);

    useEffect(() => {
        const hash = window.location.hash.substring(1);
        if (sections.includes(hash)) {
            const index = sections.indexOf(hash);
            setTimeout(() => {
                scrollToSection(hash, index);
            }, 100);
        }
    }, []);


    useEffect(() => {
        const openSpeedFactor = 1 / 1.75;
        const closeSpeedFactor = 1 / 2.25;
        const timeline = gsap.timeline({ defaults: { ease: 'power1.out' } });
        const animatedNavElements = ".nav-panel .animated-nav-element";

        gsap.set('.hamburger-open-icon span:nth-child(1)', { y: 0, rotate: 0, xPercent: 0, opacity: 1 });
        gsap.set('.hamburger-open-icon span:nth-child(2)', { y: 0, rotate: 0, xPercent: 0, opacity: 1 });

        if (menuOpen) {
            timeline
                .to("#border-circle", { strokeDashoffset: 0, duration: 0.4 * openSpeedFactor }, 0)
                .to(".header-menu-bar", { backgroundColor: "transparent", backdropFilter: "none", duration: 0 }, 0.1 * openSpeedFactor)
                .to('.nav-panel', { duration: 0.8 * openSpeedFactor, opacity: 1, y: 0, height: '100vh', minHeight: '100vh', ease: 'expo.inOut' }, 0)
                .to('.hamburger-open-icon span:nth-child(1)', {
                    y: 3.75,
                    rotate: 45,
                    transformOrigin: 'center center',
                    duration: 0.3 * openSpeedFactor,
                    ease: 'power1.inOut'
                }, 0.05 * openSpeedFactor)
                .to('.hamburger-open-icon span:nth-child(2)', {
                    y: -3.75,
                    rotate: -45,
                    transformOrigin: 'center center',
                    duration: 0.3 * openSpeedFactor,
                    ease: 'power1.inOut'
                }, "<")
                .to('.theme-btn', {
                    duration: 0.4 * openSpeedFactor,
                    y: "0%",
                    ease: 'back.out(1.7)'
                }, 0.2 * openSpeedFactor)
                .to('.lang-btn', {
                    duration: 0.4 * openSpeedFactor,
                    y: "0%",
                    ease: 'back.out(1.7)'
                }, 0.25 * openSpeedFactor)
                .to(animatedNavElements, { y: 0, opacity: 1, skewY: 0, stagger: 0.05 * openSpeedFactor, ease: 'back.out(1.7)' }, 0.3 * openSpeedFactor);
        } else {
            timeline
                .to('.nav-panel', {
                    duration: 0.5 * closeSpeedFactor,
                    opacity: 0,
                    height: 0,
                    minHeight: 'unset',
                    ease: 'expo.inOut'
                }, 0)
                .to("#border-circle", {
                    strokeDashoffset: 300,
                    duration: 0.4 * closeSpeedFactor
                }, 0)
                .to('.hamburger-open-icon span:nth-child(1)', {
                    y: 0,
                    rotate: 0,
                    duration: 0.3 * closeSpeedFactor,
                    ease: 'power1.inOut'
                }, 0.05 * closeSpeedFactor)
                .to('.hamburger-open-icon span:nth-child(2)', {
                    y: 0,
                    rotate: 0,
                    duration: 0.3 * closeSpeedFactor,
                    ease: 'power1.inOut'
                }, "<")
                .to(animatedNavElements, {
                    y: -50,
                    opacity: 0,
                    skewY: 0.7,
                    stagger: 0.02 * closeSpeedFactor,
                    ease: 'power4.out',
                    duration: 0.25 * closeSpeedFactor
                }, 0.05 * closeSpeedFactor)
                .to('.theme-btn, .lang-btn', {
                    y: "-350%",
                    ease: 'back.in(1.5)',
                    stagger: 0.04 * closeSpeedFactor,
                    duration: 0.35 * closeSpeedFactor
                }, 0.1 * closeSpeedFactor)
                .to(".header-menu-bar", {
                    clearProps: "backgroundColor,backdropFilter",
                    duration: 0
                }, 0.3 * closeSpeedFactor);
        }
        return () => {
            timeline.kill();
        };
    }, [menuOpen]);


    const handleKeyDown = useCallback((event) => {
        if (menuOpen) return;
        let newIndex = currentSectionIndex;
        const currentId = sections[currentSectionIndex];

        if (event.key === "ArrowDown") {
            newIndex = Math.min(sections.indexOf(currentId) + 1, sections.length - 1);
        } else if (event.key === "ArrowUp") {
            newIndex = Math.max(sections.indexOf(currentId) - 1, 0);
        }

        if (sections[newIndex] && newIndex !== sections.indexOf(currentId)) {
            scrollToSection(sections[newIndex], newIndex);
        }
    }, [currentSectionIndex, sections, scrollToSection, menuOpen]);

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    useEffect(() => {
        let scrollTimeout;

        const handleScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                const sectionElements = sections.map(id => document.getElementById(id)).filter(Boolean);
                let visibleIndex = -1;

                for (let i = 0; i < sectionElements.length; i++) {
                    const rect = sectionElements[i].getBoundingClientRect();
                    if (rect.top >= 0 && rect.top < window.innerHeight * 0.6) {
                        visibleIndex = i;
                        break;
                    }
                }

                if (visibleIndex !== -1) {
                    setCurrentSectionIndex(visibleIndex);
                }
            }, 150);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [sections]);

    return (
        <>
            <section className={`header-menu-bar fixed py-0 px-1 w-full z-50 bg-background-light dark:bg-background-dark bg-opacity-80 dark:bg-opacity-80 backdrop-blur-lg transition-colors duration-100`}>
                <div className="container mx-auto px-4">
                    <div className="flex justify-between items-center h-20">
                        <ScrollLink
                            id="home-link"
                            to="intro"
                            smooth={true}
                            duration={800}
                            onClick={() => scrollToSection("intro", sections.indexOf("intro"))}
                            style={{ opacity: 0 }}
                            className={`flex items-center gap-2 ml-4 md:ml-0 cursor-pointer opacity-50 hover:opacity-100 transition-all duration-200`}
                        >
                            <FaFlask alt="Logo" className="w-5 h-5 inline text-yellow-500 dark:text-yellow-200 transition-all duration-200" /> <span className="font-poppins text-2xl md:text-2xl text-text-light dark:text-text-dark transition-all duration-200">{t("nav.home")}</span>
                        </ScrollLink>

                        <div className="absolute right-4 top-3 -translate-y-0 md:relative md:top-auto md:right-auto md:-translate-y-0 flex flex-col-reverse md:flex-row items-center gap-2 md:gap-3">
                            <div
                                className="theme-btn -translate-y-[350%] md:-translate-y-[250%]"
                                onClick={toggleDarkMode}
                                title={isDarkMode ? t("nav.switchToLight", "Switch to light mode") : t("nav.switchToDark", "Switch to dark mode")}
                            >
                                <div className={`transition-all transform duration-300 active:scale-90 hover:scale-105 flex bg-button-light dark:bg-button-dark hover:bg-hover-light dark:hover:bg-hover-dark rounded-full justify-center items-center cursor-pointer w-14 h-14 overflow-hidden relative`}>
                                    <div className={`absolute transition-all duration-500 ease-in-out ${isDarkMode ? "translate-x-0 translate-y-0" : "translate-x-10 translate-y-10"}`}>
                                        <MoonIcon className={`w-6 h-6 text-text-dark`} />
                                    </div>
                                    <div className={`absolute transition-all duration-500 ease-in-out ${isDarkMode ? "-translate-x-10 translate-y-10" : "translate-x-0 translate-y-0"}`}>
                                        <SunIcon className="w-6 h-6 text-yellow-500" />
                                    </div>
                                </div>
                            </div>
                            <div
                                className="lang-btn -translate-y-[350%] md:-translate-y-[250%]"
                                onClick={toggleLanguage}
                                title={t(i18n.language === "en" ? "nav.switchToPortuguese" : "nav.switchToEnglish", `Switch to ${i18n.language === "en" ? "Portuguese" : "English"}`)}
                            >
                                <div className={`transition-all transform duration-300 active:scale-90 hover:scale-105 flex bg-button-light dark:bg-button-dark hover:bg-hover-light dark:hover:bg-hover-dark rounded-full justify-center items-center cursor-pointer w-14 h-14 overflow-hidden relative`}>
                                    <div className={`absolute transition-transform duration-300 ease-in-out ${i18n.language === "en" ? "translate-x-0 scale-100" : "translate-x-10 scale-0"}`}>
                                        <img src={ukFlag} alt="UK Flag" className="w-6 h-6 rounded-full object-cover" />
                                    </div>
                                    <div className={`absolute transition-transform duration-300 ease-in-out ${i18n.language === "pt" ? "translate-x-0 scale-100" : "-translate-x-10 scale-0"}`}>
                                        <img src={ptFlag} alt="PT Flag" className="w-6 h-6 rounded-full object-cover" />
                                    </div>
                                </div>
                            </div>
                            <div
                                className="menu-link-btn"
                                onClick={() => setMenuOpen(prev => !prev)}
                                aria-expanded={menuOpen}
                                aria-controls="full-screen-navigation-panel"
                                title={t("nav.toggleMenu", "Toggle menu")}
                            >
                                <div className={`transition-all transform duration-300 active:scale-90 hover:scale-105 flex bg-button-light dark:bg-button-dark hover:bg-hover-light dark:hover:bg-hover-dark rounded-full justify-center items-center cursor-pointer w-14 h-14 overflow-hidden relative `}>
                                    <div className="hamburger-open-icon w-[20px] flex flex-col items-center justify-center group">
                                        <span className={`h-[3px] w-full block rounded-sm bg-hr-light dark:bg-hr-dark transition-all duration-300 mb-[5px]`}></span>
                                        <span className={`h-[3px] w-full block rounded-sm bg-hr-light dark:bg-hr-dark transition-all duration-300 self-end`}></span>
                                    </div>
                                    <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                                        <circle id="border-circle" cx="50" cy="50" r="40" fill="none" strokeWidth="3.5" strokeDasharray="300" strokeDashoffset="300" className={`stroke-hr-light dark:stroke-hr-dark opacity-40`} />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section
                id="full-screen-navigation-panel"
                className={`nav-panel fixed w-full bg-bgHeader-light dark:bg-bgHeader-dark p-6 md:p-10 overflow-y-hidden z-40`}
                aria-label="Main Navigation"
                style={{ opacity: 0, height: 0, minHeight: 'unset' }}
            >
                <div className="flex flex-col md:flex-row pt-20 md:pt-24 max-w-full md:max-w-7xl mx-auto">
                    <div className="nav-items md:w-[65%] space-y-2">
                        {sections
                            .filter(sectionId => sectionId !== "intro" && sectionId !== "contacts")
                            .map((sectionId) => (
                                <div key={sectionId} className="animated-nav-element" style={{ opacity: 0, transform: 'translateY(-50px) skewY(0.7deg)' }}>
                                    <ScrollLink
                                        to={sectionId}
                                        smooth={true}
                                        duration={800}
                                        offset={0}
                                        onClick={() => scrollToSection(sectionId, sections.indexOf(sectionId))}
                                        className={`font-poppins relative block font-bold text-3xl lg:text-6xl 2xl:text-6xl py-4 lg:py-8 pl-8 cursor-pointer hover:text-textHover-light hover:dark:text-textHover-dark hover:opacity-60 hover:translate-x-1 opacity-70 transition-all duration-200 group  ${currentSectionIndex === sections.indexOf(sectionId) ? '' : 'text-background-light dark:text-background-dark [text-shadow:1px_1px_0_theme(colors.textActive.light),_-1px_-1px_0_theme(colors.textActive.light),_1px_-1px_0_theme(colors.textActive.light),_-1px_1px_0_theme(colors.textActive.light)] dark:[text-shadow:1px_1px_0_theme(colors.textActive.dark),_-1px_-1px_0_theme(colors.textActive.dark),_1px_-1px_0_theme(colors.textActive.dark),_-1px_1px_0_theme(colors.textActive.dark)]'} hover:[text-shadow:none] hover:dark:[text-shadow:none]`}
                                        aria-label={t(`nav.${sectionId}`)}
                                    >
                                        {currentSectionIndex === sections.indexOf(sectionId) && (
                                            <FiChevronRight className="absolute -left-0 md:-left-1 top-1/2 -translate-y-1/2 h-8 w-8 md:h-10 md:w-10 text-textActive-light dark:text-textActive-dark transition-all duration-200" />
                                        )}
                                        {t(`nav.${sectionId}`)}
                                        {currentSectionIndex === sections.indexOf(sectionId) ? (
                                            <span className="animate-ping">_</span>
                                        ) : null}
                                    </ScrollLink>
                                </div>
                            ))}
                    </div>

                    <div className="md:w-[55%] md:pl-10 lg:pl-16 mt-10 md:mt-0">
                        <div className="nav-socials animated-nav-element flex justify-center md:justify-start gap-5 md:gap-6 mb-8 md:mb-10" style={{ opacity: 0, transform: 'translateY(-50px) skewY(0.7deg)' }}>
                            <a href="https://www.linkedin.com/in/eduardormw/" target="_blank" rel="noopener noreferrer" className={`text-text-light dark:text-text-dark opacity-70 hover:opacity-100 dark:hover:opacity-100 hover:text-blue-700 dark:hover:text-blue-400 transition-all duration-200`} title="LinkedIn"><FaLinkedin size={26} /></a>
                            <a href="https://www.instagram.com/eduardormw" target="_blank" rel="noopener noreferrer" className={`text-text-light dark:text-text-dark opacity-70 hover:opacity-100 dark:hover:opacity-100 hover:text-pink-600 dark:hover:text-pink-400 transition-all duration-200`} title="Instagram"><FaInstagram size={26} /></a>
                            <a href="https://www.flickr.com/photos/eduardormw/albums" target="_blank" rel="noopener noreferrer" className={`text-text-light dark:text-text-dark opacity-70 hover:opacity-100 dark:hover:opacity-100 hover:text-purple-600 dark:hover:text-purple-400 transition-all duration-200`} title="Instagram"><FaFlickr size={26} /></a>
                        </div>

                        <div className="nav-contacts-desktop animated-nav-element hidden md:block text-left" style={{ opacity: 0, transform: 'translateY(-50px) skewY(0.7deg)' }}>
                            <h3 className={`font-poppins text-xl font-semibold mb-3 text-text-light dark:text-text-dark transition-all duration-200 mt-20`}>{t('contact.subtitle')}</h3>
                            <ScrollLink
                                to="contacts"
                                smooth={true}
                                duration={800}
                                offset={0}
                                onClick={() => scrollToSection("contacts", sections.indexOf("contacts"))}
                                className={`font-poppins inline-flex items-center gap-2 text-text-light dark:text-text-dark opacity-80 hover:opacity-100 dark:hover:opacity-100 hover:text-textHover-light dark:hover:text-textHover-dark text-lg group transition-all duration-200 cursor-pointer`}
                            >
                                <FaEnvelope className="w-5 h-5 inline opacity-70 group-hover:opacity-100 transition-all" />
                                {t('nav.contacts')}
                            </ScrollLink>
                        </div>
                    </div>
                </div>

                <div className="nav-contact-mobile animated-nav-element md:hidden text-center flex justify-center m-auto mt-10" style={{ opacity: 0, transform: 'translateY(-50px) skewY(0.7deg)' }}>
                    <ScrollLink
                        to="contacts"
                        smooth={true}
                        duration={800}
                        offset={0}
                        onClick={() => scrollToSection("contacts", sections.indexOf("contacts"))}
                        className={`action-button flex items-center gap-2 rounded px-4 py-2 cursor-pointer`}
                    >
                        <FaEnvelope className="w-5 h-5 inline" />
                        <span>{t('contact.subtitle')}</span>
                    </ScrollLink>
                </div>

                <div className="nav-privacy animated-nav-element flex flex-col md:flex-row gap-2 md:gap-4 justify-center items-center pt-12 pb-6 md:pt-16 text-xs " style={{ opacity: 0, transform: 'translateY(-50px) skewY(0.7deg)' }}>
                    <span className={`font-poppins text-text-light dark:text-text-dark opacity-70 transition-all duration-200`}> {t("nav.home")} © {new Date().getFullYear()}</span>
                    <button
                        onClick={handleLegalLinkClick}
                        className={`font-poppins flex items-center gap-1.5 text-text-light dark:text-text-dark opacity-70 hover:opacity-100 dark:hover:opacity-100 hover:text-textHover-light dark:hover:text-textHover-dark cursor-pointer transition-all duration-200`}
                        aria-label={t("legal.buttons.open") || "Open legal information"}
                    >
                        <ScaleIcon className="w-5 h-5 inline" /> {t("legal.buttons.open")}
                    </button>
                </div>
            </section>
        </>
    );
};

export default Header;